import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraOverlayDialog } from '@mono/ui-components/captains';
import { SpecraButton, SpecraText, SpecraTextarea } from '@mono/ui-components/ensigns';
import { SpecraCombobox, SpecraFormField, SpecraSegmentedControl } from '@mono/ui-components/lieutenants';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useDebounce } from '../../../../hooks/useDebounce';
import { deliveryClient } from '../../../../lib/connect/clients';
import { useProposeLaborCategoryMapping } from '../../../Delivery/Registries/hooks';
import { lcatAnchorKeys } from './hooks';
/** Proposes a PENDING mapping from an existing entry to this anchor; a person approves it in the review queue. */ export function MapLaborCategoryDialog({ open, onOpenChange, tenantId, anchorId, anchorTitle }) {
    const [search, setSearch] = useState('');
    const [entryId, setEntryId] = useState();
    const [relation, setRelation] = useState('EQUIVALENT');
    const [note, setNote] = useState('');
    const debounced = useDebounce(search, 250);
    const qc = useQueryClient();
    const propose = useProposeLaborCategoryMapping();
    const entries = useQuery({
        queryKey: [
            'registry',
            'lcat-map-entries',
            tenantId,
            debounced
        ],
        enabled: open && !!tenantId,
        queryFn: async ()=>(await deliveryClient.listLaborCategoryEntries({
                companyId: tenantId,
                search: debounced,
                limit: 20
            })).entries
    });
    const options = (entries.data ?? []).map((e)=>({
            value: e.id,
            label: e.title,
            description: [
                e.companyName,
                e.contractVehicleName
            ].filter(Boolean).join(' · '),
            textValue: `${e.title} ${e.companyName}`
        }));
    const reset = ()=>{
        setSearch('');
        setEntryId(undefined);
        setRelation('EQUIVALENT');
        setNote('');
    };
    const submit = async ()=>{
        if (!entryId) return;
        await propose.mutateAsync({
            companyId: tenantId,
            fromEntryId: entryId,
            toAnchorId: anchorId,
            mappingType: relation,
            evidenceJson: JSON.stringify({
                note
            })
        });
        await qc.invalidateQueries({
            queryKey: lcatAnchorKeys.all
        });
        reset();
        onOpenChange(false);
    };
    return /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
        open: open,
        onOpenChange: onOpenChange,
        title: `Map a labor category to ${anchorTitle}`,
        footer: /*#__PURE__*/ _jsxs("div", {
            className: "flex w-full items-center justify-between gap-3",
            children: [
                /*#__PURE__*/ _jsx(SpecraText, {
                    size: "meta",
                    tone: "muted",
                    children: "A person approves it in the review queue."
                }),
                /*#__PURE__*/ _jsx(SpecraButton, {
                    disabled: !entryId || propose.isPending,
                    onClick: ()=>void submit(),
                    children: "Propose mapping"
                })
            ]
        }),
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-4",
            children: [
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Labor category",
                    children: /*#__PURE__*/ _jsx(SpecraCombobox, {
                        label: "Labor category",
                        options: options,
                        value: entryId,
                        onValueChange: setEntryId,
                        searchValue: search,
                        onSearchValueChange: setSearch,
                        shouldFilter: false,
                        loading: entries.isFetching,
                        placeholder: "Search entries by title",
                        emptyMessage: "No matching labor categories."
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Relation to the anchor",
                    children: /*#__PURE__*/ _jsx(SpecraSegmentedControl, {
                        label: "Relation",
                        value: relation,
                        onValueChange: setRelation,
                        options: [
                            {
                                value: 'EQUIVALENT',
                                label: 'Equivalent'
                            },
                            {
                                value: 'BROADER',
                                label: 'Broader'
                            },
                            {
                                value: 'NARROWER',
                                label: 'Narrower'
                            }
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Evidence note",
                    children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                        value: note,
                        onChange: (e)=>setNote(e.target.value),
                        rows: 3
                    })
                })
            ]
        })
    });
}
